import React, { forwardRef, ReactNode } from 'react';

import ProgressLine from '~/pages/AnalyticsPage/components/ProgressLine/ProgressLine';
import { CssUtilityType } from '~/styled';
import { SKELETON_TEXT_HEIGHT_SMALL_PX, TINY_MARGIN_PX, useIsWidthUp } from '~/theme';
import VideoPreview from '~/ui/components/VideoPreview/VideoPreview';

import {
  DivGrow,
  Duration,
  LeftBlock,
  LeftBlockInner,
  LoadingDescription,
  LoadingTitle,
  ProgressLineWrapper,
  RightBlock,
  StyledLink,
  Title,
  Wrapper,
} from './styles';

export interface IListItem {
  imageUrl?: string | null;
  title?: string | null;
  titleLink?: string;
  subtitle?: ReactNode;
  progressPercentage?: number;
  progressTitle?: ReactNode;
  completed?: boolean;
  wrapperStyles?: CssUtilityType;
  withPlayIcon?: boolean;
  hasProgress?: boolean;
  totalDuration?: string | number;
  children?: ReactNode;
  loading?: boolean;
}

const VideoListItem = forwardRef<HTMLDivElement, IListItem>(
  (
    {
      imageUrl,
      title,
      titleLink,
      subtitle,
      progressPercentage,
      progressTitle,
      completed = false,
      wrapperStyles,
      withPlayIcon = false,
      hasProgress,
      totalDuration,
      children,
      loading,
      ...rest
    },
    ref,
  ) => {
    const isMobile = !useIsWidthUp('md');
    if (loading) {
      return (
        <Wrapper
          ref={ref}
          optionalStyles={wrapperStyles}
          hasProgress={hasProgress}
          data-testid={rest['data-testid']}
        >
          <VideoPreview loading={true} />
          <LoadingTitle
            xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
            spacer={TINY_MARGIN_PX}
            lines={2}
            lastLineWidth={70}
          />
          <LoadingDescription xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
        </Wrapper>
      );
    }

    return (
      <Wrapper
        ref={ref}
        optionalStyles={wrapperStyles}
        hasProgress={hasProgress}
        data-testid={rest['data-testid']}
      >
        <LeftBlock>
          <LeftBlockInner>
            <VideoPreview
              completed={completed}
              withPlayIcon={withPlayIcon}
              alt={title}
              imageUrl={imageUrl}
              data-testid={rest['data-testid']}
              size={isMobile ? 'sm' : 'md'}
            />
            <DivGrow>
              <Title>
                {titleLink ? (
                  <StyledLink data-testid={rest['data-testid'] + 'Link'} to={titleLink}>
                    {title}
                  </StyledLink>
                ) : (
                  title
                )}
              </Title>
              {subtitle}
            </DivGrow>
          </LeftBlockInner>
        </LeftBlock>
        <RightBlock data-testid={rest['data-testid'] + 'Progress'}>
          {(progressPercentage || progressPercentage === 0) && (
            <ProgressLineWrapper>
              <ProgressLine
                value={Math.floor(progressPercentage)}
                progressString={progressTitle}
                completed={completed}
              />
            </ProgressLineWrapper>
          )}

          {!hasProgress && <Duration>{totalDuration}</Duration>}
          {children}
        </RightBlock>
      </Wrapper>
    );
  },
);

export default VideoListItem;
